import React, { useState } from 'react';
import { NEUTRAL, onAccent } from './designTokens';
import { FreeSlotPanel, BlockForm, DAY_NAMES } from './BookingDayBand';

// T-10 del 3: veckovy där alla tider per dag visas som rader.
// Bokad = ljus accentton med namn + tjänst, ledig = streckad rad med "+ Boka",
// pågår nu = accentfylld, stängd dag = nedtonad, blockerad dag = randig med orsak.
// T-16/T-14: rubrik och navigering ligger i Bokningars mörka huvud. Klick på en ledig
// tid väljer den och visar samma panel som dagläget (Boka åt kund / Blockera tiden).

const DAY_NAMES_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const FREE_SHOWN = 4; // lediga tider som visas innan "+N lediga"

function generateSlots(schedule, duration) {
  if (!schedule) return [];
  const slots = [];
  const [startH, startM] = schedule.start_time.split(':').map(Number);
  const [endH, endM] = schedule.end_time.split(':').map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  const step = duration + (schedule.break_duration || 0);
  let current = startMin;
  while (current + duration <= endMin) {
    const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    slots.push({ start: fmt(current), end: fmt(current + duration) });
    current += step;
  }
  return slots;
}

function BookingCalendarView({
  theme,
  selectedWeekStart,
  prevWeek,
  nextWeek,
  thisWeek,
  getWeekDates,
  getDateStr,
  getDayBookings,
  schedules,
  blockedTimes,
  isTimeBooked,
  isSlotBlocked,
  defaultDuration,
  setSelectedDate,
  openClientPicker,
  onOpenClient,
  planBlock,
  blockSeries,
}) {
  const [picked, setPicked] = useState(null); // { dateStr, start, end, date } vald ledig tid
  const [blockingPicked, setBlockingPicked] = useState(false);
  const [expanded, setExpanded] = useState({}); // dateStr -> true när alla lediga visas
  const [hoverId, setHoverId] = useState(null);
  const todayStr = getDateStr(new Date());
  const now = new Date();
  const nowStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;


  return (
    <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
      {/* Färgnyckel */}
      <div className="fr-calendar-legend" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: NEUTRAL.muted, padding: '10px 16px', borderBottom: `1px solid ${theme.card_border}` }}>
        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: theme.accent_soft_bg, border: `1px solid ${theme.accent_soft_text}33` }} />Bokad
        <span style={{ width: '10px', height: '10px', borderRadius: '3px', border: `1px dashed ${NEUTRAL.faint}`, marginLeft: '10px' }} />Ledig
        <span style={{ width: '10px', height: '10px', borderRadius: '3px', marginLeft: '10px', background: `repeating-linear-gradient(135deg, ${NEUTRAL.line} 0 3px, ${theme.card_bg} 3px 6px)`, border: `1px solid ${NEUTRAL.line}` }} />Blockerad
        <span style={{ marginLeft: 'auto' }}>Klicka på en dag för dagläget</span>
      </div>

      {/* Week grid */}
      <div className="fr-calendar-week-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {getWeekDates().map((date, idx) => {
          const dateStr = getDateStr(date);
          const isToday = dateStr === todayStr;
          const isPast = dateStr < todayStr;
          const schedule = schedules.find(s => s.day_of_week === date.getDay() && s.is_active);
          const blockedAll = blockedTimes.find(bt => bt.date === dateStr && bt.all_day);
          const dayBookings = getDayBookings(dateStr);

          // Rader: bokningar + lediga tider, sorterade på starttid
          const rows = dayBookings.map(b => ({ kind: 'booking', start: b.start_time?.slice(0, 5), end: b.end_time?.slice(0, 5), booking: b }));
          if (schedule && !blockedAll) {
            generateSlots(schedule, defaultDuration)
              .filter(s => !isTimeBooked(dateStr, s.start, s.end) && !isSlotBlocked(dateStr, s.start, s.end))
              .forEach(s => rows.push({ kind: 'free', start: s.start, end: s.end }));
            // Blockerade tider (ej heldag) visas randiga så att terapeuten ser dem i veckovyn.
            blockedTimes
              .filter(bt => bt.date === dateStr && !bt.all_day && bt.start_time)
              .forEach(bt => rows.push({ kind: 'blocked', start: bt.start_time.slice(0, 5), end: bt.end_time?.slice(0, 5), block: bt }));
          }
          rows.sort((a, b) => a.start.localeCompare(b.start));
          const freeRows = rows.filter(r => r.kind === 'free');
          const hiddenFree = expanded[dateStr] ? 0 : Math.max(0, freeRows.length - FREE_SHOWN);
          let freeSeen = 0;
          const visible = rows.filter(r => {
            if (r.kind !== 'free') return true;
            freeSeen += 1;
            return expanded[dateStr] || freeSeen <= FREE_SHOWN;
          });

          const columnBg = blockedAll
            ? `repeating-linear-gradient(135deg, ${theme.card_bg} 0 8px, ${NEUTRAL.page} 8px 16px)`
            : isToday ? theme.accent_muted : !schedule ? NEUTRAL.page : theme.card_bg;

          return (
            <div
              key={dateStr}
              className="fr-calendar-day-card"
              style={{
                borderRight: idx < 6 ? `1px solid ${theme.card_border}` : 'none',
                display: 'flex', flexDirection: 'column', minWidth: 0,
                background: columnBg,
                opacity: isPast && !isToday ? 0.6 : 1,
              }}
            >
              {/* Day header – klick öppnar dagvyn */}
              <div
                className="fr-calendar-day-header"
                onClick={() => setSelectedDate(date)}
                title="Öppna dagen"
                style={{ padding: '12px 10px 8px', textAlign: 'center', borderBottom: isToday ? `2px solid ${theme.accent}` : `1px solid ${theme.card_border}`, cursor: 'pointer' }}
              >
                <div style={{ fontSize: '11px', fontWeight: isToday ? '700' : '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: isToday ? theme.accent : !schedule ? NEUTRAL.faint : NEUTRAL.muted }}>
                  {DAY_NAMES_SHORT[date.getDay()]}{isToday ? ' · idag' : ''}
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: isToday ? '800' : '700', color: isToday ? theme.accent : !schedule ? NEUTRAL.faint : NEUTRAL.ink, lineHeight: 1.2 }}>
                  {date.getDate()}
                </div>
              </div>

              {/* Content */}
              <div className="fr-calendar-day-content" style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {blockedAll ? (
                  <div style={{ textAlign: 'center', paddingTop: '8px' }}>
                    <span style={{ display: 'inline-block', borderRadius: '999px', padding: '4px 10px', background: NEUTRAL.ink, color: '#FFFFFF', fontSize: '11px', fontWeight: '600' }}>
                      Blockerad{blockedAll.reason ? ` · ${blockedAll.reason}` : ''}
                    </span>
                  </div>
                ) : !schedule ? (
                  <div style={{ padding: '16px 8px', textAlign: 'center', fontSize: '12px', color: NEUTRAL.faint }}>Stängt</div>
                ) : (
                  <>
                    {visible.map(r => {
                      if (r.kind === 'booking') {
                        const b = r.booking;
                        const ongoing = isToday && r.start <= nowStr && nowStr < r.end;
                        const serviceLabel = b.service?.title || b.service_title_snapshot || '';
                        const mins = b.service?.duration;
                        return (
                          <div
                            key={b.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => (onOpenClient && b.client ? onOpenClient(b.client) : setSelectedDate(date))}
                            onKeyDown={e => { if (e.key === 'Enter' && onOpenClient && b.client) onOpenClient(b.client); }}
                            onMouseEnter={() => setHoverId(b.id)}
                            onMouseLeave={() => setHoverId(null)}
                            title={`Öppna ${b.client?.name || 'kunden'}`}
                            style={{
                              borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', minWidth: 0,
                              background: ongoing ? theme.accent : hoverId === b.id ? theme.card_bg : theme.accent_soft_bg,
                              color: ongoing ? onAccent(theme.accent) : NEUTRAL.ink,
                              outline: hoverId === b.id ? `2px solid ${theme.accent}` : 'none',
                              outlineOffset: '-1px',
                              boxShadow: ongoing || hoverId === b.id ? `0 4px 12px ${theme.accent}40` : 'none',
                              transform: hoverId === b.id ? 'translateY(-1px)' : 'none',
                              transition: 'transform 0.12s, box-shadow 0.12s, background 0.12s',
                            }}
                          >
                            <div style={{ fontSize: '12px', fontWeight: '700', lineHeight: 1.3 }}><span style={{ color: ongoing ? 'inherit' : theme.accent_soft_text }}>{r.start}</span> {b.client?.name || 'Okänd'}</div>
                            <div style={{ fontSize: '11px', color: ongoing ? 'inherit' : NEUTRAL.text, opacity: ongoing ? 0.85 : 1, lineHeight: 1.3 }}>
                              {serviceLabel}{mins ? ` · ${mins} min` : ''}{ongoing ? ' · pågår nu' : ''}
                            </div>
                            {hoverId === b.id && (
                              <div style={{ fontSize: '11px', fontWeight: '600', color: ongoing ? 'inherit' : theme.accent_text, marginTop: '4px' }}>Öppna kundkort →</div>
                            )}
                          </div>
                        );
                      }
                      if (r.kind === 'blocked') {
                        return (
                          <div
                            key={`blocked-${r.block.id}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedDate(date)}
                            onKeyDown={e => { if (e.key === 'Enter') setSelectedDate(date); }}
                            title="Öppna dagen för att avblockera"
                            style={{
                              borderRadius: '8px', padding: '8px 10px', fontSize: '12px', cursor: 'pointer', minWidth: 0,
                              border: `1px solid ${NEUTRAL.line}`, color: NEUTRAL.muted,
                              background: `repeating-linear-gradient(135deg, ${theme.card_bg} 0 6px, ${NEUTRAL.page} 6px 12px)`,
                            }}
                          >
                            <div style={{ fontWeight: '700', color: NEUTRAL.text, lineHeight: 1.3 }}>{r.start} Blockerad{r.block.series_id ? ' ↻' : ''}</div>
                            {r.block.reason && <div style={{ fontSize: '11px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.block.reason}</div>}
                          </div>
                        );
                      }
                      const past = isPast || (isToday && r.start < nowStr);
                      const isPicked = picked && picked.dateStr === dateStr && picked.start === r.start;
                      return (
                        <button
                          type="button"
                          key={r.start}
                          className="fr-calendar-free-slot"
                          disabled={past}
                          aria-pressed={!!isPicked}
                          onClick={() => { setBlockingPicked(false); setPicked(isPicked ? null : { dateStr, start: r.start, end: r.end, date }); }}
                          title={past ? 'Passerad tid' : 'Boka eller blockera'}
                          style={{
                            borderRadius: '8px', padding: '8px 10px', fontSize: '12px', fontFamily: 'inherit', textAlign: 'left', width: '100%',
                            border: isPicked ? `2px solid ${theme.accent}` : `1px dashed ${past ? NEUTRAL.line : NEUTRAL.faint}`,
                            boxShadow: isPicked ? `0 0 0 3px ${theme.accent_soft_bg}` : 'none',
                            background: theme.card_bg,
                            color: past ? NEUTRAL.faint : NEUTRAL.text,
                            cursor: past ? 'default' : 'pointer',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <span>{r.start}</span>
                          {!past && <span style={{ fontSize: '11px', color: theme.accent_text, fontWeight: '600', whiteSpace: 'nowrap' }}>+ Boka</span>}
                        </button>
                      );
                    })}
                    {hiddenFree > 0 && (
                      <button
                        type="button"
                        onClick={() => setExpanded(prev => ({ ...prev, [dateStr]: true }))}
                        style={{ background: 'none', border: 'none', color: NEUTRAL.muted, fontSize: '11px', cursor: 'pointer', padding: '4px', fontFamily: 'inherit' }}
                      >
                        +{hiddenFree} lediga
                      </button>
                    )}
                    {rows.length === 0 && (
                      <div style={{ padding: '12px 8px', textAlign: 'center', fontSize: '12px', color: NEUTRAL.faint }}>Fullbokat</div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {picked && (
        <div className="fr-calendar-picked" style={{ padding: '14px 20px', borderTop: `1px solid ${theme.card_border}`, background: '#FBFCFD' }}>
          {blockingPicked ? (
            <BlockForm
              theme={theme}
              date={picked.date}
              start={picked.start}
              end={picked.end}
              plan={(repeat, until) => planBlock({ dateStr: picked.dateStr, start: picked.start, end: picked.end }, repeat, until)}
              onSubmit={async (o) => { await blockSeries({ dateStr: picked.dateStr, start: picked.start, end: picked.end }, o); setPicked(null); setBlockingPicked(false); }}
              onCancel={() => setBlockingPicked(false)}
            />
          ) : (
            <FreeSlotPanel
              theme={theme}
              label={`Ledig · ${DAY_NAMES[picked.date.getDay()].toLowerCase()} ${picked.date.getDate()} ${MONTHS[picked.date.getMonth()]} ${picked.start}–${picked.end}`}
              onBook={() => { openClientPicker(picked.dateStr, picked.start, picked.end); setPicked(null); }}
              onBlockClick={() => setBlockingPicked(true)}
              onClose={() => setPicked(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default BookingCalendarView;
